import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ZeosButton } from "../../../components/Shared/ZeosButton";
import { ZeosInput } from "../../../components/Shared/ZeosInput";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import {
  setUtilityManualDraft,
  utilityManualDraftSelector,
} from "../../../redux-store/OnboardingSlice";
import { Analytics } from "../../../services/analytics/Analytics";
import { IZeosUser } from "../../../services/auth/IZeosUser";
import { ZeosFontSize } from "../../../services/helpers/ThemeHelpers";
import { createLogger } from "../../../services/Logger";
import { IOnboardStatus } from "../../../services/nav/Interfaces";
import { Document } from "../../../services/zeos-api/collections/Document";
import { Utility } from "../../../services/zeos-api/collections/Utility";
import {
  DocumentUploadRequest,
  DocumentUploadResponse,
} from "../../../services/zeos-api/contracts/Document";
import { UapiManualSaveRequest } from "../../../services/zeos-api/contracts/Uitility";
import { IZeosApiResponse } from "../../../services/zeos-api/contracts/ZeosApi";
import {
  BILL_UPLOAD_ACCEPT,
  fileKey,
  getDocumentTypeId,
} from "../../../services/zeos-api/helpers/DocumentUploadHelpers";
import { OnboardContainer } from "../layout/OnboardContainer";

interface IUtilityForm {
  electric: string;
  gas: string;
}
interface IUapiManualScreenProps {}

const log = createLogger("UapiManual");

export const UapiManualScreen: React.FC<IUapiManualScreenProps> = () => {
  const api = useContext(ZeosApiContext);
  const dispatch = useDispatch();
  const utilityManualDraft = useSelector(utilityManualDraftSelector);

  const [form, setForm] = useState<IUtilityForm>(
    utilityManualDraft ?? { electric: "", gas: "" }
  );

  const [uapiManualId, setUapiManualId] = useState<number | undefined>(
    utilityManualDraft?.uapiManualId
  );
  // `files` is never draft-persisted (File isn't serializable) — user
  // re-attaches after navigating back, same rule as the property draft.
  const [files, setFiles] = useState<File[]>([]);
  const [zeosUser, setZeosUser] = useState<IZeosUser>();
  const [isBackPage, setIsBackPage] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  const propertyId = useMemo(() => {
    return (
      zeosUser?.customerClaims?.[0]?.companies?.[0]?.properties?.[0]
        ?.propertyId || null
    );
  }, [zeosUser]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUserCallback = (user: IZeosUser): void => setZeosUser(user);
  const handleStatusCallback = (status: IOnboardStatus): void =>
    setIsBackPage(status.isBack);

  // Persist form input (and the created record's ID, once known) to the
  // draft as the user types, so it survives navigating away and back. No
  // rehydration effect exists on this screen to race against, so no loading
  // guard is needed.
  useEffect(() => {
    dispatch(setUtilityManualDraft({ ...form, uapiManualId }));
  }, [form, uapiManualId, dispatch]);

  const handleChange =
    (field: keyof IUtilityForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (v === "" || /^(\d+(\.\d{0,2})?)$/.test(v)) {
        setForm((prev) => ({ ...prev, [field]: v }));
      }
    };

  const openFilePicker = (): void => fileInputRef.current?.click();

  // Append newly-picked files to the existing selection so the user can add
  // bills across multiple picks — e.g. one month at a time. We skip only
  // exact re-adds of the same file, identified by name + size + lastModified.
  // Utility providers often export each month under the same filename (and
  // sometimes the same size), but the file's lastModified timestamp differs
  // per download, so distinct months are kept; re-picking the identical file
  // (same name/size/lastModified) is the one case we drop.
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length > 0) {
      setFiles((prev) => {
        const seen = new Set(prev.map(fileKey));
        const merged = [...prev];
        for (const f of picked) {
          const key = fileKey(f);
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(f);
          }
        }
        return merged;
      });
    }
    setError("");
    // Reset the input so picking the same file again still fires onChange.
    e.target.value = "";
  };

  const removeFile = (index: number): void => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const isValid = useMemo(() => {
    const e = form.electric.trim();
    const g = form.gas.trim();
    return e !== "" || g !== "" || files.length > 0;
  }, [form, files]);

  const formContent = (
    <div className="font-sans">
      <div className="col-span-12 px-2">
        <p
          className="text-lg font-semibold text-[#333]"
        >
          Manual Utility Data Entry
        </p>
      </div>

      <div className="grid grid-cols-2 px-2 gap-6">
        <div className="relative col-span-1">
          <ZeosInput
            name="gas"
            label="Estimated Monthly Gas"
            type="text"
            placeholder="e.g., 75.00"
            value={form.gas}
            onChange={handleChange("gas")}
            size="lg"
          />
          <div className="absolute top-[49px] left-3 text-zeos-black">$</div>
        </div>
        <div className="relative col-span-1">
          <ZeosInput
            name="electric"
            label="Estimated Monthly Electric"
            type="text"
            placeholder="e.g., 120.00"
            value={form.electric}
            onChange={handleChange("electric")}
            size="lg"
          />
          <div className="absolute top-[49px] left-3 text-zeos-black">$</div>
        </div>
      </div>

      <div className="px-2 mt-4">
        <input
          ref={fileInputRef}
          className="hidden"
          type="file"
          multiple
          accept={BILL_UPLOAD_ACCEPT}
          onChange={onFileChange}
        />
        <div
          role="button"
          tabIndex={0}
          onClick={openFilePicker}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") openFilePicker();
          }}
          className="inline-block"
        >
          <ZeosButton
            label={
              files.length > 0 ? "Add More Statements" : "Upload Your Statements"
            }
            mode="action"
            type="button"
            hackWidth
          />
        </div>
        {files.length > 0 && (
          <ul className="mt-3 space-y-1">
            {files.map((f, i) => (
              <li
                key={`${f.name}:${f.size}:${i}`}
                className="flex items-center gap-3 text-sm text-zeos-black"
              >
                <span className="break-all">
                  {f.name} ({Math.ceil(f.size / 1024)} KB)
                </span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-red-600 underline shrink-0"
                  aria-label={`Remove ${f.name}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
        {!isValid && (
          <div className="text-amber-700 text-sm mt-2">
            Enter <b>Gas</b> or <b>Electric</b> or <b>upload a bill</b> to
            continue.
          </div>
        )}
      </div>
    </div>
  );

  const handleNext = async (doNavigate: () => void): Promise<void> => {
    const e = form.electric.trim();
    const g = form.gas.trim();
    if (!(e !== "" || g !== "" || files.length > 0)) {
      setError("Enter Gas or Electric or upload a bill to continue.");
      return;
    }

    if (propertyId === null) {
      setError(
        "No active property found (latestPropertyId missing or invalid)."
      );
      return;
    }

    setSaving(true);
    setError("");
    try {
      // Each uploaded bill is persisted as its own row in common__document
      // with its own documentId, associated to the building via entityId
      // (= propertyId) + documentCategoryId 12 (UtilityBill). That property
      // association is the source of truth for "which bills belong to this
      // building" and is how they'd be retrieved for display later
      // (document/get by entityTypeId 20 + entityId).
      //
      // poc__uapimanualrecord.documentId is a single many-to-one FK, so the
      // manual record can reference only one document; we link the first
      // upload. This link is not currently read back anywhere, so it has no
      // user-visible effect — the entityId association already covers every
      // bill. Intentionally kept as first-only rather than migrating the
      // record to a many relationship (see 3885 discussion).
      let documentId: number | null = null;

      if (files.length > 0) {
        const uploads: DocumentUploadRequest[] = files.map((f) => ({
          entityTypeId: 20,
          entityId: propertyId,
          documentTypeId: getDocumentTypeId(f),
          documentCategoryId: 12,
          file: f,
        }));

        const resp: IZeosApiResponse<DocumentUploadResponse[]> =
          await Document.createMultipleAsync(uploads);
        const status = String(
          (resp as any)?.zeosStatus ?? (resp as any)?.ZeosStatus ?? ""
        ).toLowerCase();
        if ((resp as any)?.error || status === "error") {
          throw new Error((resp as any)?.message || "Upload failed");
        }

        const data = (resp as any).Data ?? (resp as any).data ?? resp;
        const uploaded: DocumentUploadResponse[] = Array.isArray(data)
          ? data
          : [];
        const documentIds = uploaded
          .map((d: any) => d?.documentId ?? d?.DocumentId)
          .map((id: any) =>
            typeof id === "number" ? id : typeof id === "string" ? Number(id) : NaN
          )
          .filter((id: number): id is number => Number.isFinite(id));

        if (documentIds.length !== files.length) {
          const apiMessage = (resp as any)?.message || (resp as any)?.Message;
          throw new Error(
            apiMessage
              ? `Upload failed: ${apiMessage}`
              : "Upload response missing one or more documentIds"
          );
        }

        documentId = documentIds[0];
      }

      // API currently requires non-null decimals; send 0 for blank fields
      // until product confirms whether bill-only nulls should be allowed.
      const payload: UapiManualSaveRequest = {
        propertyId,
        estimatedMonthlyGas: g === "" ? 0 : parseFloat(g),
        estimatedMonthlyElectric: e === "" ? 0 : parseFloat(e),
        documentId,
      };

      const zeosUserId = zeosUser?.zeosUserId;
      if (!zeosUserId) {
        throw new Error("Missing user context");
      }

      // A record already exists for this session (identity-based decision,
      // — update it instead of creating another.
      const saveResp =
        Number(uapiManualId) > 0
          ? await Utility.manualUpdateAsync(
              zeosUserId,
              uapiManualId as number,
              payload
            )
          : await Utility.manualSaveAsync(zeosUserId, payload);

      const saveStatus = String(
        (saveResp as any)?.zeosStatus ?? (saveResp as any)?.ZeosStatus ?? ""
      ).toLowerCase();
      if (saveStatus === "error") {
        throw new Error((saveResp as any)?.message || "Save failed");
      }

      Analytics.capture("utility_connected", { method: "manual" });

      const savedId = saveResp.data?.uapiManualId;
      if (savedId) {
        setUapiManualId(savedId);
      }

      doNavigate();
    } catch (err: any) {
      log.error("Manual utility save failed:", err);
      setError(err?.message ?? "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardContainer
      content={formContent}
      contentType="display"
      pageSlug="uapi-manual"
      onNext={isValid && !saving ? handleNext : undefined}
      userCallback={handleUserCallback}
      statusCallback={handleStatusCallback}
    />
  );
};
